import {
  BreadcrumbComponent,
  Button,
  Caret,
  Subtitle,
  Title,
} from '../../components/shared';
import useMenuHandler from '../../lib/useMenuHandler';
import getBreadcrumbMenu from '../../lib/BreadcrumbMenu';
import prevIcon from '../../images/prev-image.png';
import nextIcon from '../../images/next-image.png';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import LineTagButton from '../../components/lines/LineTagButton';
import { IOysterLineDetail } from '../../entities/farms.entities';
import { ICycleResource } from '../../entities/growing.entities';
import HarvestPredictButton from '../../components/lines/HarvestPredictButton';
import { selectLang } from '../../store/ui/ui.selector';
import { translate } from '../../lib/lang.helper';
import {
  SelectFarmsByFarmID,
  SelectIsEditable,
  SelectIsFarmable,
} from '../../store/extra/extra.selector';
import { useState } from 'react';
import OysterLineProbModal from '../../components/farm-util/OysterLineProbModal';
import DotsMenu from '../../components/shared/dropdown-menu/DotsMenu';
import metaIcon from '../../images/history_icon.png';
import HarvestDateModal from '../../components/farm-modals/HarvestDateModal';
import OysterLineHeaders from '../../components/lines-headers/OysterLineHeaders';
import LineHeadersModal from '../../components/lines-headers/LineHeadersModal';
import LineCropTypeModal from '../../components/lines/LineCropTypeModal';
import LineManageButtonsView from './LineManageButtonsView';
import { selectAccount } from '../../store/auth/auth.selector';

interface Props {
  lineData: IOysterLineDetail;
  cyclePart: ICycleResource | null;
  isPrevEnabled: boolean;
  isNextEnabled: boolean;
  onClickPrevGroup: () => void;
  onClickNextGroup: () => void;
  showEditLineModal: () => void;
  onSeedingClick: () => void;
  onCatchSpatClick: () => void;
  onAssessmentClick: () => void;
  onMaintenanceClick: () => void;
  onHarvestClick: () => void;
  onReturnClick: () => void;
  onExtraSeedClick: () => void;
  onFloatingManageClick: () => void;
  checkedHistoryView: number;
  onHistoryViewClick: () => void;
  onMetaEditClick: () => void;
  loadLine: () => void;
}

const OysterLineDesktop = ({
  lineData,
  cyclePart: curCycle,
  isPrevEnabled,
  isNextEnabled,
  onClickPrevGroup,
  onClickNextGroup,
  showEditLineModal,
  onSeedingClick,
  onCatchSpatClick,
  onAssessmentClick,
  onMaintenanceClick,
  onHarvestClick,
  onReturnClick,
  onExtraSeedClick,
  onFloatingManageClick,
  loadLine,
  checkedHistoryView,
  onMetaEditClick,
  onHistoryViewClick,
}: Props) => {
  const lang = useSelector(selectLang);
  const isEditable = SelectIsEditable(lineData.farm_id);
  const isFarmable = SelectIsFarmable(lineData.farm_id);
  const account = useSelector(selectAccount);
  const { redirectToLine } = useMenuHandler();
  const curFarm = SelectFarmsByFarmID(lineData.farm_id).find(
    x => x.id === lineData.farm_id,
  );
  const lines = curFarm?.lines ?? [];
  const breadcrumbItems = getBreadcrumbMenu('FARM_LINE', lang, {
    FARM_ID: lineData.farm_id,
    LINE_ID: lineData.id,
    FARM_NAME: lineData?.farm_name,
    LINE_NAME: lineData?.line_name,
  }).slice(0, -1);

  const curL = lines.findIndex(x => x.id === lineData.id);
  const prevL = (curL - 1 + lines.length) % lines.length;
  const nextL = (curL + 1) % lines.length;

  const mainSeed = curCycle?.main_seed;

  const [visibleProb, setVisibleProb] = useState(false);
  const [visEstDate, setVisEstDate] = useState(false);
  const [visEditHeader, setVisEditHeader] = useState(false);
  const [visCropType, setVisCropType] = useState(false);

  const getMenuItems = () => {
    let result = [
      {
        label: translate(lang, 'Edit line'),
        onClick: showEditLineModal,
      },
      {
        label: translate(lang, 'Modify probability'),
        onClick: () => setVisibleProb(true),
      },
      {
        label: translate(lang, 'Change harvest date'),
        onClick: () => setVisEstDate(true),
      },
      {
        label: translate(lang, 'Edit line headers'),
        onClick: () => setVisEditHeader(true),
      },
    ];
    if (!lineData.growing_cycle) {
      result = [
        ...result,
        {
          label: translate(lang, 'Change crop type'),
          onClick: () => setVisCropType(true),
        },
      ];
    }
    return result;
  };

  return (
    <div className='farm-line-desktop-template'>
      <div className='pt-28 pb-28 d-flex justify-between align-center'>
        <div className='d-flex' style={{ alignItems: 'baseline' }}>
          <BreadcrumbComponent items={breadcrumbItems} />
          {lines && (
            <div className='ml-17'>
              <span
                className='prev-link'
                onClick={() =>
                  redirectToLine(lineData.farm_id, lines[prevL].id)
                }
              >
                <img src={prevIcon} alt='prev' />
              </span>
              <span className='line-name'>
                {translate(lang, 'Line %s', lineData.line_name)}
              </span>
              <span
                className='next-link'
                onClick={() =>
                  redirectToLine(lineData.farm_id, lines[nextL].id)
                }
              >
                <img src={nextIcon} alt='next' />
              </span>
            </div>
          )}
        </div>
        <div className='d-flex justify-between align-center'>
          {lineData.growing_cycle?.taxonomy_groups &&
            lineData.growing_cycle.taxonomy_groups.length > 0 && (
              <Button
                color='green'
                size={1}
                width='middle'
                type='bordered'
                className='mr-8 mxw-270'
              >
                {`${translate(lang, 'Taxonomies')}: ${lineData.growing_cycle.taxonomy_groups.map(x => x.name).join(', ')}`}
              </Button>
            )}
          <div className='d-flex justify-between align-center white-card-small pt-3 pb-3'>
            <Button
              color='blue'
              size={3}
              width='default'
              type='transparent'
              className='mr-26'
              name='prev'
              onClick={onClickPrevGroup}
              disabled={!isPrevEnabled}
              onlyIconDisabled
            >
              <Caret color='#5A607F' direction='left' />
            </Button>
            <Subtitle
              size={4}
              color='black'
              align='left'
              fontWeight={500}
              disabled={!curCycle}
            >
              {mainSeed?.season_name ?? translate(lang, 'Empty')}
            </Subtitle>
            <Button
              color='blue'
              size={3}
              width='default'
              type='transparent'
              className='ml-26'
              name='next'
              onClick={onClickNextGroup}
              disabled={!isNextEnabled}
              onlyIconDisabled
            >
              <Caret color='#5A607F' direction='right' />
            </Button>
          </div>
          <Button
            color='blue'
            size={0}
            width='default'
            type='bordered'
            className='ml-8'
            iconOnly
            onClick={onMetaEditClick}
            disabled={!isEditable}
          >
            <img src={metaIcon} alt='meta-icon' style={{ width: 40 }} />
          </Button>
          <Button
            color='blue'
            size={1}
            width='middle'
            type='bordered'
            className='ml-16'
            onClick={onHarvestClick}
            disabled={!mainSeed?.is_growing || !isFarmable}
          >
            {translate(lang, 'Harvest Complete')}
          </Button>
          {!!account?.have_customers && (
            <Button
              color='blue'
              size={1}
              width='middle'
              type='bordered'
              onClick={onReturnClick}
              disabled={!mainSeed?.is_growing || !isFarmable}
            >
              {translate(lang, 'Spat Harvest')}
            </Button>
          )}
        </div>
      </div>
      <div className='white-card pt-28 pr-16 pb-28 pl-24 mb-16'>
        <div className='d-flex justify-between'>
          <div>
            <Title size={5} color='black-3' align='default' fontWeight={500}>
              {translate(lang, 'Line %s', lineData.line_name)}
            </Title>
            {!!lineData.growing_cycle?.last_harvest ? (
              <div style={{ marginTop: '20px' }}>
                <Link
                  to={`/harvest/${lineData.growing_cycle.last_harvest.id}`}
                  style={{
                    fontStyle: 'italic',
                    textDecorationLine: 'underline',
                  }}
                >
                  <Subtitle
                    size={5}
                    color='gray'
                    align='default'
                    fontWeight={400}
                  >
                    {translate(lang, '_line_partially_harvested')}
                  </Subtitle>
                </Link>
                <Subtitle
                  size={5}
                  color='#000'
                  align='default'
                  fontWeight={500}
                >
                  {translate(
                    lang,
                    'Harvested %s so far',
                    `${lineData.growing_cycle.total_harvested_basket_count} ${lineData.farming_method}`,
                  )}
                </Subtitle>
              </div>
            ) : (
              !!lineData.growing_cycle?.last_harvest && (
                <div style={{ marginTop: '10px' }}>
                  <Link
                    to={`/harvest/${lineData.growing_cycle.last_harvest.id}`}
                    style={{
                      fontStyle: 'italic',
                      textDecorationLine: 'underline',
                    }}
                  >
                    <Subtitle
                      size={5}
                      color='gray'
                      align='default'
                      fontWeight={400}
                    >
                      {translate(lang, 'View last harvest')}
                    </Subtitle>
                  </Link>
                </div>
              )
            )}
            <div className='mt-17'>
              <LineTagButton lineData={lineData} loadLine={loadLine} />
            </div>
          </div>
          <OysterLineHeaders line={lineData} cycle={curCycle} />
          {isEditable && <DotsMenu items={getMenuItems()} />}
        </div>
        {!!lineData.growing_cycle?.predicted_date_harvest && (
          <div style={{ textAlign: 'right' }}>
            <HarvestPredictButton lineData={lineData} />
          </div>
        )}
      </div>
      <LineManageButtonsView
        lineData={lineData}
        cyclePart={curCycle}
        checkedHistoryView={checkedHistoryView}
        onHistoryViewClick={onHistoryViewClick}
        onFloatingManageClick={onFloatingManageClick}
        onSeedingClick={onSeedingClick}
        onAssessmentClick={onAssessmentClick}
        onCatchSpatClick={onCatchSpatClick}
        onMaintenanceClick={onMaintenanceClick}
        onExtraSeedClick={onExtraSeedClick}
        loadLine={loadLine}
      />
      {visibleProb && (
        <OysterLineProbModal
          visible={true}
          lineID={lineData.id}
          onClose={() => setVisibleProb(false)}
        />
      )}
      {visEstDate && !!curCycle && (
        <HarvestDateModal
          visible={true}
          cycleData={curCycle}
          onClose={() => setVisEstDate(false)}
          onUpdate={loadLine}
          cropType={'OYSTER'}
        />
      )}
      {visEditHeader && (
        <LineHeadersModal
          type='OYSTER'
          visible={true}
          onClose={() => setVisEditHeader(false)}
          line={lineData}
          farm={curFarm}
        />
      )}
      {visCropType && (
        <LineCropTypeModal
          visible={true}
          lineData={lineData}
          onClose={() => setVisCropType(false)}
          loadLine={loadLine}
        />
      )}
    </div>
  );
};

export default OysterLineDesktop;
